import React, { useState } from 'react';
import { Grid, Typography, Box, Button, TextField } from '@mui/material';
import CustomModal from 'components/CustomModal';
import { DateTime } from 'luxon';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { BankingService } from 'services';
import { useSnackbar } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

type Props = {
  open: boolean;
  accountType: string;
  onClose: () => void;
};

const bankingService = new BankingService();

const ExportModal = ({ open, accountType, onClose }: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<DateTime | null>(null);

  const maxDate = DateTime.now().minus({ months: 1 }).endOf('month');

  const startDate = value?.startOf('month');
  const endDate = value?.endOf('month');
  const formattedDate = value ? `${startDate?.toFormat('dd/MM/yyyy')} — ${endDate?.toFormat('dd/MM/yyyy')}` : '';

  const canExport = value !== null && !loading;

  const handleExport = async () => {
    if (!value || !startDate || !endDate) return;

    setLoading(true);

    try {
      await bankingService.exportTransactionsReport({
        startDate: startDate.toISODate() || '',
        endDate: endDate.toISODate() || '',
        accountType,
      });

      enqueueSnackbar('Relatório de transações solicitado com sucesso. Verifique seu e-mail em alguns minutos.', { variant: 'success' });

      onClose();
    } catch {
      enqueueSnackbar('Erro ao solicitar o relatório de transações. Tente novamente mais tarde.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal open={open} onClose={onClose} sx={{ p: '30px', minWidth: '492px' }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight={600} mb={1}>
            Enviar extrato por e-mail
          </Typography>

          <Typography variant="body1" fontWeight={500} color="#4E4D4D">
            Selecione o mês para geração do extrato e o formato desejado
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="pt-br">
            <DatePicker<DateTime>
              views={['year', 'month']}
              openTo="month"
              value={value}
              onChange={(newValue) => setValue(newValue)}
              maxDate={maxDate}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: 'Informe mês e ano',
                    value: formattedDate,
                  }}
                  fullWidth
                />
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>

            <Button variant="contained" onClick={handleExport} disabled={!canExport}>
              Enviar
            </Button>
          </Box>
        </Grid>
      </Grid>
    </CustomModal>
  );
};

export default ExportModal;
